import { request } from "../client";

import type {
  AuthResponse,
  AnonymousAuthRequest,
  AnonymousWebSessionRequest,
  AnonymousWebSessionResponse,
  LoginRequest,
  RegisterRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UserProfile,
} from "../types/user";
import type { ContactSettings, RegistrationSettings } from "@lingoshade/domain";

export function createAuthEndpoints(baseUrl: string): {
  getRegistrationSettings: () => Promise<RegistrationSettings>;
  getContactSettings: () => Promise<ContactSettings>;
  anonymous: (req: AnonymousAuthRequest) => Promise<AuthResponse>;
  createWebSession: (
    req: AnonymousWebSessionRequest,
  ) => Promise<AnonymousWebSessionResponse>;
  login: (req: LoginRequest) => Promise<AuthResponse>;
  register: (req: RegisterRequest) => Promise<AuthResponse>;
  logout: (token?: string | null) => Promise<{ success: boolean }>;
  refresh: (token?: string | null) => Promise<AuthResponse>;
  verify: (
    token?: string | null,
  ) => Promise<{ valid: boolean; user?: UserProfile }>;
  me: (token?: string | null) => Promise<{ user: UserProfile }>;
  updateAnonymousPreferences: (
    preferences: Record<string, unknown>,
    token?: string | null,
  ) => Promise<{ user: UserProfile }>;
  upgradeAnonymous: (
    payload: Record<string, unknown>,
    token?: string | null,
  ) => Promise<{ user: UserProfile; token?: string; message?: string }>;
  verifyEmail: (payload: VerifyEmailRequest) => Promise<VerifyEmailResponse>;
  resendVerification: (
    payload: ResendVerificationRequest,
  ) => Promise<ResendVerificationResponse>;
  forgotPassword: (
    payload: ForgotPasswordRequest,
  ) => Promise<ForgotPasswordResponse>;
  resetPassword: (
    payload: ResetPasswordRequest,
  ) => Promise<ResetPasswordResponse>;
  changePassword: (
    payload: ChangePasswordRequest,
    token?: string | null,
  ) => Promise<ChangePasswordResponse>;
  getOAuthProviders: () => Promise<{ providers: string[] }>;
  startOAuthLink: (
    provider: string,
    token?: string | null,
  ) => Promise<{ state: string }>;
  getLinkedOAuthProviders: (token?: string | null) => Promise<{
    providers: Array<{ provider: string; email: string; linkedAt: string }>;
  }>;
  unlinkOAuthProvider: (
    provider: string,
    token?: string | null,
  ) => Promise<{ message: string }>;
  setUserPassword: (
    payload: { password: string },
    token?: string | null,
  ) => Promise<{ success: boolean; message: string }>;
  changeUserPassword: (
    payload: { currentPassword: string; newPassword: string },
    token?: string | null,
  ) => Promise<{ success: boolean; message: string }>;
  getPasswordStatus: (
    token?: string | null,
  ) => Promise<{ hasPassword: boolean }>;
  removePassword: (
    payload: { currentPassword: string },
    token?: string | null,
  ) => Promise<{ success: boolean; message: string }>;
  checkActivePromoCodes: () => Promise<{ hasActiveCodes: boolean }>;
} {
  return {
    getContactSettings(): Promise<ContactSettings> {
      return request<ContactSettings>(baseUrl, "/auth/contact-settings");
    },
    getRegistrationSettings(): Promise<RegistrationSettings> {
      return request<RegistrationSettings>(
        baseUrl,
        "/auth/registration-settings",
      );
    },
    /**
     * Анонимная аутентификация для мобильных устройств
     */
    anonymous(req: AnonymousAuthRequest): Promise<AuthResponse> {
      return request<AuthResponse>(baseUrl, "/auth/anonymous", {
        method: "POST",
        body: req,
      });
    },

    /**
     * Создание анонимной веб-сессии
     */
    createWebSession(
      req: AnonymousWebSessionRequest,
    ): Promise<AnonymousWebSessionResponse> {
      return request<AnonymousWebSessionResponse>(
        baseUrl,
        "/auth/anonymous/web-session",
        {
          method: "POST",
          body: req,
        },
      );
    },

    /**
     * Обычная аутентификация по email/password
     */
    login(req: LoginRequest): Promise<AuthResponse> {
      return request<AuthResponse>(baseUrl, "/auth/login", {
        method: "POST",
        body: req,
      });
    },

    /**
     * Регистрация нового пользователя
     */
    register(req: RegisterRequest): Promise<AuthResponse> {
      return request<AuthResponse>(baseUrl, "/auth/register", {
        method: "POST",
        body: req,
      });
    },

    /**
     * Выход из системы
     */
    logout(token?: string | null): Promise<{ success: boolean }> {
      return request<{ success: boolean }>(baseUrl, "/auth/logout", {
        method: "POST",
        token,
      });
    },

    /**
     * Обновление токена
     */
    refresh(token?: string | null): Promise<AuthResponse> {
      return request<AuthResponse>(baseUrl, "/auth/refresh", {
        method: "POST",
        token,
      });
    },

    /**
     * Проверка действительности токена
     */
    verify(
      token?: string | null,
    ): Promise<{ valid: boolean; user?: UserProfile }> {
      return request<{ valid: boolean; user?: UserProfile }>(
        baseUrl,
        "/auth/verify",
        {
          method: "GET",
          token,
        },
      );
    },

    /**
     * Получить текущего пользователя по токену
     */
    me(token?: string | null): Promise<{ user: UserProfile }> {
      return request<{ user: UserProfile }>(baseUrl, "/auth/me", {
        method: "GET",
        token,
      });
    },

    /**
     * Обновить предпочтения анонимного пользователя
     */
    updateAnonymousPreferences(
      preferences: Record<string, unknown>,
      token?: string | null,
    ): Promise<{ user: UserProfile }> {
      return request<{ user: UserProfile }>(
        baseUrl,
        "/auth/anonymous/update-preferences",
        {
          method: "POST",
          body: preferences,
          token,
        },
      );
    },

    /**
     * Апгрейд анонимного пользователя до полноценного аккаунта
     */
    upgradeAnonymous(
      payload: Record<string, unknown>,
      token?: string | null,
    ): Promise<{ user: UserProfile; token?: string; message?: string }> {
      return request<{ user: UserProfile; token?: string; message?: string }>(
        baseUrl,
        "/auth/anonymous/upgrade",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /** Email verification (token POST) */
    verifyEmail(payload: VerifyEmailRequest): Promise<VerifyEmailResponse> {
      return request<VerifyEmailResponse>(baseUrl, "/auth/verify-email", {
        method: "POST",
        body: payload,
      });
    },

    /** Resend email verification */
    resendVerification(
      payload: ResendVerificationRequest,
    ): Promise<ResendVerificationResponse> {
      return request<ResendVerificationResponse>(
        baseUrl,
        "/auth/resend-verification",
        {
          method: "POST",
          body: payload,
        },
      );
    },

    /** Forgot password flow */
    forgotPassword(
      payload: ForgotPasswordRequest,
    ): Promise<ForgotPasswordResponse> {
      return request<ForgotPasswordResponse>(baseUrl, "/auth/forgot-password", {
        method: "POST",
        body: payload,
      });
    },

    resetPassword(
      payload: ResetPasswordRequest,
    ): Promise<ResetPasswordResponse> {
      return request<ResetPasswordResponse>(baseUrl, "/auth/reset-password", {
        method: "POST",
        body: payload,
      });
    },

    changePassword(
      payload: ChangePasswordRequest,
      token?: string | null,
    ): Promise<ChangePasswordResponse> {
      return request<ChangePasswordResponse>(baseUrl, "/auth/change-password", {
        method: "POST",
        body: payload,
        token,
      });
    },

    /**
     * Get enabled OAuth providers
     */
    getOAuthProviders(): Promise<{ providers: string[] }> {
      return request<{ providers: string[] }>(
        baseUrl,
        "/auth/oauth/providers",
        {
          method: "GET",
        },
      );
    },

    startOAuthLink(
      provider: string,
      token?: string | null,
    ): Promise<{ state: string }> {
      return request<{ state: string }>(
        baseUrl,
        `/auth/oauth/${provider}/link-start`,
        {
          method: "POST",
          token,
        },
      );
    },

    /**
     * Get linked OAuth providers for current user
     */
    getLinkedOAuthProviders(token?: string | null): Promise<{
      providers: Array<{ provider: string; email: string; linkedAt: string }>;
    }> {
      return request<{
        providers: Array<{ provider: string; email: string; linkedAt: string }>;
      }>(baseUrl, "/auth/oauth/linked", {
        method: "GET",
        token,
      });
    },

    /**
     * Unlink OAuth provider
     */
    unlinkOAuthProvider(
      provider: string,
      token?: string | null,
    ): Promise<{ message: string }> {
      return request<{ message: string }>(baseUrl, `/auth/oauth/${provider}`, {
        method: "DELETE",
        token,
      });
    },

    /**
     * Set password (for users without password) - new password management
     */
    setUserPassword(
      payload: { password: string },
      token?: string | null,
    ): Promise<{ success: boolean; message: string }> {
      return request<{ success: boolean; message: string }>(
        baseUrl,
        "/auth/password/set",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Change password (for users with existing password) - new password management
     */
    changeUserPassword(
      payload: { currentPassword: string; newPassword: string },
      token?: string | null,
    ): Promise<{ success: boolean; message: string }> {
      return request<{ success: boolean; message: string }>(
        baseUrl,
        "/auth/password/change",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Check if user has password - new password management
     */
    getPasswordStatus(
      token?: string | null,
    ): Promise<{ hasPassword: boolean }> {
      return request<{ hasPassword: boolean }>(
        baseUrl,
        "/auth/password/status",
        {
          method: "GET",
          token,
        },
      );
    },

    /**
     * Remove password (requires OAuth backup) - new password management
     */
    removePassword(
      payload: { currentPassword: string },
      token?: string | null,
    ): Promise<{ success: boolean; message: string }> {
      return request<{ success: boolean; message: string }>(
        baseUrl,
        "/auth/password/remove",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Check if there are any active promo codes (for conditional UI display)
     */
    checkActivePromoCodes(): Promise<{ hasActiveCodes: boolean }> {
      return request<{ hasActiveCodes: boolean }>(
        baseUrl,
        "/auth/promo-codes/check",
        { method: "GET" },
      );
    },
  };
}
